import React, { useState } from 'react'
import {
  CBadge,
  CButton,
  CFormInput,
  CFormSelect,
  CFormSwitch,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react-pro'
import ToastComponent from 'src/components/common/TaostComponent'
import EmailQuotaService from 'src/service/EmailQuotaService'
import './GameNotification.css'

// The two scheduled reminders differ only in their template id, wording and test endpoint, so
// they share one table instead of one accordion and one single-row table each.
const REMINDERS = [
  {
    templateId: 48,
    name: 'Fantasy Round Reminder',
    description: 'Reminds members to pick their team before the round locks.',
    placeholder: 'Set hours (before round starts)',
    scheduleLabel: (hours) => `${hours} hour${Number(hours) === 1 ? '' : 's'} before round starts`,
    countKey: 'fantasyRoundReminderCount',
    sendTest: (email) => EmailQuotaService.testFantasyRoundReminderBeforeRoundStart(email),
  },
  {
    templateId: 50,
    name: 'Fantasy Round Results & Reminder',
    description: 'Shares the round results and nudges members towards the next round.',
    placeholder: 'Set hours (after end of round)',
    scheduleLabel: (hours) => `${hours} hour${Number(hours) === 1 ? '' : 's'} after round ends`,
    countKey: 'fantasyResultCount',
    sendTest: (email) => EmailQuotaService.testFantasyRoundReminderAfterOneDay(email),
  },
]

const HOUR_OPTIONS = [...Array(24).keys()].map((num) => num + 1)

const AutomatedEmails = ({
  alertCount,
  hourValue,
  enableDisableStatus,
  handleHourChange,
  handleEnableDisableChangeStatus,
  handleSaveEmailNotificationSettingUpdated,
}) => {
  // One test-email modal and one confirm modal serve both rows; `active` says which row opened it.
  const [testFor, setTestFor] = useState(null)
  const [testEmail, setTestEmail] = useState('')
  const [sending, setSending] = useState(false)

  const [confirm, setConfirm] = useState(null)

  const closeTest = () => {
    setTestFor(null)
    setTestEmail('')
  }

  const handleSendTestEmail = () => {
    if (!testEmail) {
      ToastComponent('Please enter an email address', 'error')
      return
    }
    setSending(true)
    testFor.sendTest({ testEmail }).then((res) => {
      setSending(false)
      ToastComponent(res.message, res.success ? 'success' : 'error')
      if (res.success) closeTest()
    })
  }

  const changeSchedule = (reminder, value) => {
    setConfirm({ type: 'hour', reminder, value })
  }

  const changeStatus = (reminder, checked) => {
    const id = String(reminder.templateId)
    // Enabling without a schedule would leave the reminder with nothing to fire on.
    if (checked && (!hourValue[id] || hourValue[id] === '0')) {
      ToastComponent('Please set a notification time before enabling this reminder.', 'error')
      return
    }
    setConfirm({ type: 'switch', reminder, value: checked })
  }

  const applyConfirm = () => {
    const { type, reminder, value } = confirm
    const id = String(reminder.templateId)
    const durationObj = {}

    if (type === 'hour') {
      handleHourChange({ target: { value } }, id)
      durationObj[reminder.templateId] = value

      // Clearing the schedule switches the reminder off, so it cannot sit enabled with no time.
      if (!value || value === '0') {
        handleEnableDisableChangeStatus({ target: { checked: false } }, id)
      }
      handleSaveEmailNotificationSettingUpdated(reminder.templateId, durationObj)
    }

    if (type === 'switch') {
      handleEnableDisableChangeStatus({ target: { checked: value } }, id)
      durationObj[reminder.templateId] = hourValue[id]
      handleSaveEmailNotificationSettingUpdated(reminder.templateId, durationObj)
    }

    setConfirm(null)
  }

  return (
    <>
      <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
        <table className="table align-middle mb-0">
          <thead>
            <tr>
              <th style={{ width: '32%' }}>Email</th>
              <th style={{ width: '26%' }}>When it sends</th>
              <th style={{ width: '18%' }}>Status</th>
              <th style={{ width: '10%' }}>Sent</th>
              <th style={{ width: '14%' }}>Test</th>
            </tr>
          </thead>
          <tbody>
            {REMINDERS.map((reminder) => {
              const id = String(reminder.templateId)
              const hours = hourValue?.[id]
              const enabled = !!enableDisableStatus?.[id]

              return (
                <tr key={reminder.templateId}>
                  <td>
                    <span className="gn-email-name">{reminder.name}</span>
                    <span className="gn-email-desc">{reminder.description}</span>
                  </td>
                  <td>
                    <CFormSelect
                      className="gn-schedule-select"
                      aria-label={`When ${reminder.name} sends`}
                      value={hours || ''}
                      onChange={(e) => changeSchedule(reminder, e.target.value)}
                    >
                      <option value="">{reminder.placeholder}</option>
                      {HOUR_OPTIONS.map((num) => (
                        <option key={num} value={num}>
                          {num}
                        </option>
                      ))}
                    </CFormSelect>
                    {hours && hours !== '0' && (
                      <span className="gn-schedule-hint">{reminder.scheduleLabel(hours)}</span>
                    )}
                  </td>
                  <td>
                    <span className="gn-status">
                      <CFormSwitch
                        id={`gn-switch-${reminder.templateId}`}
                        checked={enabled}
                        onChange={(e) => changeStatus(reminder, e.target.checked)}
                        aria-label={`${enabled ? 'Disable' : 'Enable'} ${reminder.name}`}
                      />
                      <CBadge
                        color={enabled ? 'success' : 'secondary'}
                        shape="rounded-pill"
                        className="px-2 py-1"
                      >
                        {enabled ? 'Enabled' : 'Disabled'}
                      </CBadge>
                    </span>
                  </td>
                  <td className="gn-num">{alertCount?.[reminder.countKey] ?? 0}</td>
                  <td>
                    <CButton
                      color="primary"
                      variant="outline"
                      size="sm"
                      onClick={() => setTestFor(reminder)}
                    >
                      Test email
                    </CButton>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <CModal visible={!!testFor} onClose={closeTest}>
        <CModalHeader>
          <CModalTitle>Send test email</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className="gn-panel-note mb-2">
            Sends a preview of <strong>{testFor?.name}</strong> to one address. It does not count
            against your quota.
          </p>
          <CFormInput
            type="email"
            placeholder="Enter test email address"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            aria-label="Test email address"
          />
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={closeTest}>
            Cancel
          </CButton>
          <CButton color="primary" onClick={handleSendTestEmail} disabled={sending}>
            {sending ? 'Sending...' : 'Send test'}
          </CButton>
        </CModalFooter>
      </CModal>

      <CModal visible={!!confirm} onClose={() => setConfirm(null)}>
        <CModalHeader>
          <CModalTitle>Confirm change</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {confirm?.type === 'hour' &&
            (confirm.value ? (
              <>
                Send <strong>{confirm.reminder.name}</strong>{' '}
                {confirm.reminder.scheduleLabel(confirm.value)}?
              </>
            ) : (
              <>
                Clearing the schedule will also switch <strong>{confirm.reminder.name}</strong> off.
                Continue?
              </>
            ))}

          {confirm?.type === 'switch' && (
            <>
              Are you sure you want to {confirm.value ? 'enable' : 'disable'}{' '}
              <strong>{confirm.reminder.name}</strong>?
            </>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setConfirm(null)}>
            Cancel
          </CButton>
          <CButton color="primary" onClick={applyConfirm}>
            Confirm
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default AutomatedEmails
